import sum from "lodash/sum";
import uniq from "lodash/uniq";
import uniqBy from "lodash/uniqBy";
import { createSlice } from "@reduxjs/toolkit";
// utils
import axios from "../../utils/axios";
import { collection, doc, getDocs, onSnapshot, query, where } from "firebase/firestore";
import { DB } from "src/auth/FirebaseContext";
import {
  BILLING_COLLECTION,
  COURSE_CATEGORY_COLLECTION,
  COURSE_CLASS_COLLECTION,
  SCHEDULE_COURSES_COLLECTION,
} from "src/constants/collectionsIds";
import { courseCategoryType } from "src/utils/constants";
import moment from "moment";

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: null,
  products: [],
  generalBilling: [],
  product: null,
  checkout: {
    activeStep: 0,
    cart: [],
    subtotal: 0,
    total: 0,
    discount: 0,
    shipping: 0,
    billing: null,
    totalItems: 0,
  },
};

const slice = createSlice({
  name: "product",
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // GET PRODUCTS
    getProductsSuccess(state, action) {
      state.isLoading = false;
      state.products = action.payload;
    },
    //GET GENERAL BILLING
    getGeneralBillingSuccess(state, action) {
      state.isLoading = false;
      state.generalBilling = action.payload;
    },

    // GET PRODUCT
    getProductSuccess(state, action) {
      state.isLoading = false;
      state.product = action.payload;
    },

    // CHECKOUT
    getCart(state, action) {
      const cart = action.payload;

      const totalItems = sum(cart.map((product) => product.quantity));
      const subtotal = sum(cart.map((product) => product.price * product.quantity));
      state.checkout.cart = cart;
      state.checkout.discount = state.checkout.discount || 0;
      state.checkout.shipping = state.checkout.shipping || 0;
      state.checkout.billing = state.checkout.billing || null;
      state.checkout.subtotal = subtotal;
      state.checkout.total = subtotal - state.checkout.discount;
      state.checkout.totalItems = totalItems;
    },

    addToCart(state, action) {
      const newProduct = action.payload;
      const isEmptyCart = !state.checkout.cart.length;

      if (isEmptyCart) {
        state.checkout.cart = [...state.checkout.cart, newProduct];
      } else {
        state.checkout.cart = state.checkout.cart.map((product) => {
          const isExisted = product.id === newProduct.id;

          if (isExisted) {
            return {
              ...product,
              colors: uniq([...product.colors, ...newProduct.colors]),
              quantity: product.quantity + 1,
            };
          }

          return product;
        });
      }
      state.checkout.cart = uniqBy([...state.checkout.cart, newProduct], "id");
      state.checkout.totalItems = sum(state.checkout.cart.map((product) => product.quantity));
    },

    deleteCart(state, action) {
      const updateCart = state.checkout.cart.filter((product) => product.id !== action.payload);

      state.checkout.cart = updateCart;
    },

    resetCart(state) {
      state.checkout.cart = [];
      state.checkout.billing = null;
      state.checkout.activeStep = 0;
      state.checkout.total = 0;
      state.checkout.subtotal = 0;
      state.checkout.discount = 0;
      state.checkout.shipping = 0;
      state.checkout.totalItems = 0;
    },

    backStep(state) {
      state.checkout.activeStep -= 1;
    },

    nextStep(state) {
      state.checkout.activeStep += 1;
    },

    gotoStep(state, action) {
      const step = action.payload;
      state.checkout.activeStep = step;
    },

    increaseQuantity(state, action) {
      const productId = action.payload;

      const updateCart = state.checkout.cart.map((product) => {
        if (product.id === productId) {
          return {
            ...product,
            quantity: product.quantity + 1,
          };
        }
        return product;
      });

      state.checkout.cart = updateCart;
    },

    decreaseQuantity(state, action) {
      const productId = action.payload;
      const updateCart = state.checkout.cart.map((product) => {
        if (product.id === productId) {
          return {
            ...product,
            quantity: product.quantity - 1,
          };
        }
        return product;
      });

      state.checkout.cart = updateCart;
    },

    createBilling(state, action) {
      state.checkout.billing = action.payload;
    },

    applyDiscount(state, action) {
      const discount = action.payload;
      state.checkout.discount = discount;
      state.checkout.total = state.checkout.subtotal - discount;
    },

    applyShipping(state, action) {
      const shipping = action.payload;
      state.checkout.shipping = shipping;
      state.checkout.total = state.checkout.subtotal - state.checkout.discount + shipping;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  getCart,
  addToCart,
  resetCart,
  gotoStep,
  backStep,
  nextStep,
  deleteCart,
  createBilling,
  applyShipping,
  applyDiscount,
  increaseQuantity,
  decreaseQuantity,
  getGeneralBillingSuccess,
} = slice.actions;

// ----------------------------------------------------------------------

export function getProducts() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const ref = collection(DB, COURSE_CATEGORY_COLLECTION);
      const response = await getDocs(ref);

      dispatch(
        slice.actions.getProductsSuccess(
          response.docs
            .map((doc) => ({ ...doc.data(), id: doc.id }))
            .sort((a, b) => {
              return Number(a.order || 0) - Number(b.order || 0);
            }),
        ),
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export const getCoursesByRegions = () => {
  return async (dispatch, state) => {
    const { region } = state().course?.checkout;
    dispatch(slice.actions.startLoading());
    try {
      const ref = collection(DB, COURSE_CATEGORY_COLLECTION);

      const promises = [getDocs(ref)];
      if (region?.id) {
        const ref = collection(DB, COURSE_CLASS_COLLECTION);
        const queryByRegion = query(
          ref,
          where("regionId", "==", region?.id),
          where("startDateTime", ">=", moment().format("YYYY-MM-DD")),
        );
        promises.push(getDocs(queryByRegion));
      }
      let [courses, classs] = await Promise.all(promises);
      classs = classs?.docs?.map((doc) => ({ ...doc.data(), id: doc.id })) || [];
      courses = courses.docs
        .map((doc) => ({ ...doc.data(), id: doc.id }))
        .filter((course) => {
          if (course.id === courseCategoryType.drivingLessons) return true;
          if (!region?.id) return true;
          return classs.find((item) => item.courseType === course.id);
        });
      dispatch(
        slice.actions.getProductsSuccess(
          courses.sort((a, b) => {
            return Number(a.order || 0) - Number(b.order || 0);
          }),
        ),
      );
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export function getGeneralBilling() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const ref = collection(DB, BILLING_COLLECTION);
      const response = await getDocs(ref);
      // console.log( response.docs.map((doc) => ({ ...doc.data(), id: doc.id })))
      dispatch(
        slice.actions.getGeneralBillingSuccess(
          response.docs.map((doc) => ({ ...doc.data(), id: doc.id })),
        ),
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export const observeBillingByBillingId = (billingId, callback) => {
  let docRef = doc(collection(DB, BILLING_COLLECTION), billingId);
  return onSnapshot(docRef, async (snap) => {
    if (!snap.exists()) return callback?.(null);
    let data = { id: snap.id, ...snap.data() };
    callback?.(data);
  });
};
export const observeBillingByTransactionId = (transactionId, callback) => {
  let queryParams = query(
    collection(DB, BILLING_COLLECTION),
    where("transactionId", "==", transactionId),
  );
  return onSnapshot(queryParams, async (snap) => {
    if (snap.empty) return callback?.(null);

    const [doc] = snap.docs;
    let data = { id: doc.id, ...doc.data() };
    callback?.(data);
  });
};
export const observeScheduleCoursesByBillingId = (billingId, callback) => {
  let queryParams = query(
    collection(DB, SCHEDULE_COURSES_COLLECTION),
    where("billingId", "==", billingId),
  );
  return onSnapshot(queryParams, async (snap) => {
    if (snap.empty) return callback?.(null);

    const [doc] = snap.docs;
    let data = { id: doc.id, ...doc.data() };
    callback?.(data);
  });
};

// ----------------------------------------------------------------------

export function getProduct(name) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get("/api/products/product", {
        params: { name },
      });
      dispatch(slice.actions.getProductSuccess(response.data.product));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}
